/**
 * A driver's payment methods and the operator's veto (W9-DRIVER-PAYOUT-AND-METHODS, D470, ADR-0088; DevMobility F-006),
 * on the driver's record (Pessoas › Motorista), in the scope's market.
 *
 * Each method the driver chose, his market admits or defaults is listed with his acceptance and, when an operator vetoed
 * it, the veto's reason code. "Vetar" takes the method away from him (restrictive only — dispatch denies it with
 * `PAYMENT_METHOD_VETOED_BY_OPERATOR` and his app shows it locked); "Liberar" lifts it. Both need a reason from the stable
 * list and `driver:suspend`; the read needs `driver:view`. The server is the authority — the panel shows its answer.
 */
import { type ReactElement, useCallback, useEffect, useState } from 'react';
import { type DriverPaymentMethodsAdminView } from '@vello/api-client';
import { Badge, Button, Select, Text, Ink, RecordPanel, RecordRow } from '@vello/web-theme';
import { useAdmin } from '../session';
import { useConsole } from '../console';
import { useT } from '../i18n/provider';
import { useVocab } from '../i18n/vocab';
import { LoadingBlock } from './loading-block';

/** The stable veto reasons the console offers (the server accepts any `[A-Z0-9_]` code). */
export const PAYMENT_VETO_REASONS = ['OPERATOR_VETO_FRAUD_RISK', 'OPERATOR_VETO_CASH_DISPUTES', 'OPERATOR_VETO_DRIVER_REQUEST', 'OPERATOR_VETO_OTHER'] as const;

export function PersonPaymentMethodsPanel({ personId }: { personId: string }): ReactElement {
  const { runtime, scope } = useAdmin();
  const { toast } = useConsole();
  const { t } = useT();
  const { fail, v } = useVocab();
  const market = scope.current.city;
  const [view, setView] = useState<DriverPaymentMethodsAdminView | undefined>(undefined);
  const [error, setError] = useState<string | undefined>(undefined);
  const [reason, setReason] = useState<string>(PAYMENT_VETO_REASONS[0]);
  const [busy, setBusy] = useState(false);
  const vetoReasonLabel = (code: string | undefined): string => v('payment.operator_veto_reason', code);

  const load = useCallback(async (): Promise<void> => {
    if (market === undefined) return;
    const res = await runtime.driverPaymentMethods.get({ marketId: market, driverId: personId });
    if (res.ok) {
      setView(res.value);
      setError(undefined);
    } else setError(t('pmveto.refused', { code: fail(res.error) }));
  }, [runtime, market, personId, t]);

  useEffect(() => {
    void load();
  }, [load]);

  const act = async (method: string, vetoed: boolean): Promise<void> => {
    if (market === undefined) return;
    setBusy(true);
    const res = await runtime.driverPaymentMethods.veto({ marketId: market, driverId: personId, method, vetoed, reasonCode: vetoed ? reason : 'OPERATOR_VETO_LIFTED' });
    setBusy(false);
    if (!res.ok) {
      toast({ message: t('pmveto.refused', { code: fail(res.error) }), icon: 'shieldAlert', tone: 'warning' });
      return;
    }
    toast({ message: t('pmveto.done'), icon: 'check', tone: 'success' });
    await load();
  };

  return (
    <RecordPanel title={t('pmveto.title')} data-testid="person-payment-methods">
        {market === undefined ? (
          <Text as="p" role="caption" tone="secondary">{t('pmveto.needsMarket')}</Text>
        ) : error !== undefined ? (
          <Text as="p" role="caption" tone="secondary">{error}</Text>
        ) : view === undefined ? (
          <LoadingBlock lines={2} />
        ) : (
          <>
            <RecordRow as="label" tight>
              <Text role="caption" tone="secondary">{t('pmveto.reason')}</Text>
              <Select id="pmveto-reason" value={reason} onChange={(e) => setReason(e.target.value)}>
                {PAYMENT_VETO_REASONS.map((r) => (
                  <option key={r} value={r}>{v('payment.operator_veto_reason', r)}</option>
                ))}
              </Select>
            </RecordRow>
            {view.methods.map((m) => (
              <RecordRow key={m.method} data-testid={`pmveto-row-${m.method}`}>
                <Ink strong>{v('payment.method', m.method)}</Ink>
                {m.vetoed === true ? (
                  <Badge tone="danger">{t('pmveto.vetoed', { code: vetoReasonLabel(m.vetoReasonCode) })}</Badge>
                ) : (
                  <Badge tone={m.accepts ? 'success' : 'neutral'}>{t(m.accepts ? 'pmveto.accepts' : 'pmveto.declines')}</Badge>
                )}
                <span className="vello-grow" />
                {m.vetoed === true ? (
                  <Button variant="secondary" disabled={busy} onClick={() => void act(m.method, false)}>{t('pmveto.lift')}</Button>
                ) : (
                  <Button variant="danger" disabled={busy} onClick={() => void act(m.method, true)}>{t('pmveto.veto')}</Button>
                )}
              </RecordRow>
            ))}
          </>
        )}
    </RecordPanel>
  );
}
